/*
 * app.js — /true-size-map: drag a country across a Mercator map and watch it
 * shrink or grow to its true size.
 *
 * What is on the page, and why each piece exists (research of 2026-09-24,
 * thetruesize.com, truesize.net, guesswhereyouare and a dozen clones, plus
 * what their users complain about):
 *   · the move is a rigid rotation of the ellipsoid's authalic sphere
 *     (core.js) — area and shape are preserved exactly, north stays up, a
 *     handle turns the shape;
 *   · a shape can never be dragged into the pole band Mercator cannot draw —
 *     «I dragged India to the South Pole and it covered the whole world» is
 *     the most repeated complaint about the category;
 *   · the address bar is REPLACED, never pushed: one drag is not fifty
 *     history entries (three separate HN threads about thetruesize);
 *   · an equal-area comparison beside the map: Mercator is the problem being
 *     shown, so the honest answer is given in a projection that keeps area;
 *   · search in any of the ten languages, and the regions of every country
 *     (Yakutia, Texas, Bavaria) loaded when asked for;
 *   · a «which is bigger?» quiz built from pairs Mercator gets wrong, for the
 *     classroom — teachers are the audience that keeps coming back.
 */
(function () {
    'use strict';
    var CFG = window.TS_CFG || {};
    var SLUG = 'true-size-map';
    var $ = function (id) { return document.getElementById(id); };
    var S = {};
    try { S = JSON.parse(($('tsStrings') || {}).textContent || '{}'); } catch (_) {}
    function t(k, v) { var s = S[k] != null ? String(S[k]) : k; if (v) for (var x in v) s = s.split(':' + x).join(v[x]); return s; }
    var LOC = CFG.locale || 'en';
    var T = window.TrueSize;
    var NF = new Intl.NumberFormat(LOC, { maximumFractionDigits: 0 });
    var NF1 = new Intl.NumberFormat(LOC, { maximumFractionDigits: 1 });
    var NF2 = new Intl.NumberFormat(LOC, { maximumSignificantDigits: 3 });
    // Okabe–Ito without yellow: tells apart for every common colour-vision deficiency
    var PALETTE = ['#0072B2', '#D55E00', '#009E73', '#CC79A7', '#E69F00', '#56B4E9', '#8E4EC6', '#444444'];

    function ae(ev, d) { try { if (window.__ae) window.__ae(ev, Object.assign({ slug: SLUG }, d || {})); } catch (_) {} }
    function load(k, d) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (_) { return d; } }
    function save(k, v) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (_) {} }
    var imperial = load('units', /^en-(US|LR)$/.test(navigator.language || '') ? 'mi' : 'km') === 'mi';

    // ── data ──────────────────────────────────────────────────────────────
    var catalog = [], byKey = {}, regionsLoaded = {}, worldReady = null;
    function name(sh) { return (sh.names && (sh.names[LOC] || sh.names.en)) || sh.key; }
    function fetchJson(url) { return fetch(url, { credentials: 'omit' }).then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); }); }
    function loadWorld() {
        return fetchJson(CFG.worldUrl).then(function (j) {
            catalog = T.decode(j);
            catalog.forEach(function (s) { byKey[s.key] = s; });
        });
    }
    function loadRegions(a3) {
        if (regionsLoaded[a3]) return Promise.resolve(regionsLoaded[a3]);
        var file = (a3 === 'RUS' || a3 === 'UKR') ? a3 + '-' + CFG.pov : a3;
        return fetchJson(CFG.regionsBase + file + '.json?v=' + (CFG.dataV || 1)).then(function (j) {
            var list = T.decode(j);
            list.forEach(function (s) { s.parent = a3; byKey[s.key] = s; });
            regionsLoaded[a3] = list;
            ae('feature_use', { f: 'regions', c: a3, n: list.length });
            return list;
        });
    }

    // ── state ─────────────────────────────────────────────────────────────
    var items = [];        // { id, shape, lat, lon, rot, color, placed }
    var selected = null;
    var nextId = 1;
    var quiz = null;

    /* «29.9 million» is grammar, not arithmetic: the word agrees with the number
     * (ru «1,5 миллиона», fr «1,5 million», pt «1,5 milhão») and Korean counts
     * in 10⁴ (2990만, not «29.9백만»). The browser's compact-long format knows
     * all ten; the translated «:n million» strings stay as the fallback. */
    var NFC = null;
    try {
        NFC = new Intl.NumberFormat(LOC, { notation: 'compact', compactDisplay: 'long', maximumSignificantDigits: 3 });
        if (NFC.format(2e6) === NF.format(2e6)) NFC = null;
    } catch (e) { NFC = null; }
    function fmtArea(km2) {
        if (km2 == null) return '—';
        var v = imperial ? km2 / 2.589988 : km2, u = imperial ? t('u_mi2') : t('u_km2');
        // fr/es/pt say «millions DE km²»: the joiner is the locale's (big_area)
        if (v >= 1e6) return NFC ? t('big_area', { n: NFC.format(v), u: u }) : t('u_million', { n: NF2.format(v / 1e6) }) + ' ' + u;
        return (v < 10 ? NF1 : NF).format(v) + ' ' + u;
    }
    function fmtPop(p) {
        if (!p) return null;
        if (p >= 1e6 && NFC) return NFC.format(p);
        if (p >= 1e9) return t('pop_billion', { n: NF2.format(p / 1e9) });
        if (p >= 1e6) return t('pop_million', { n: NF2.format(p / 1e6) });
        return NF.format(Math.round(p / 1000) * 1000);
    }
    /* «in 14 times» agrees with its number in Russian and Ukrainian (раз / раза /
     * разів), and a fraction takes its own form: plural rules pick the word. */
    var PR = typeof Intl !== 'undefined' && Intl.PluralRules ? new Intl.PluralRules(LOC) : null;
    function timesWord(r) {
        var shown = r >= 10 ? Math.round(r) : Math.round(r * 10) / 10;
        var cat = PR ? PR.select(shown) : 'other';
        return S['x_' + cat] != null ? S['x_' + cat] : (S.x_other || '');
    }
    function ratioText(a, b) { // a vs b, both items
        var r = a.shape.area / b.shape.area;
        if (r >= 1) return t('ratio_bigger', { a: name(a.shape), b: name(b.shape), r: fmtRatio(r), x: timesWord(r) });
        return t('ratio_smaller', { a: name(a.shape), b: name(b.shape), r: fmtRatio(1 / r), x: timesWord(1 / r) });
    }
    // Greenland is 0.026 people per km², not «0»
    var NFD = new Intl.NumberFormat(LOC, { maximumSignificantDigits: 2 });
    function fmtDensity(v) { return (v < 10 ? NFD : NF).format(v); }
    function fmtRatio(r) { return r >= 10 ? NF.format(r) : NF1.format(r); }

    function addShape(shape, opt) {
        opt = opt || {};
        T.prepare(shape);
        var c = shape.geo.c;
        var it = { id: nextId++, shape: shape, lat: opt.lat != null ? opt.lat : c[0], lon: opt.lon != null ? opt.lon : c[1], rot: normRot(opt.rot || 0), color: PALETTE[(items.length) % PALETTE.length] };
        if (!T.fits(shape, it.lat, it.lon, it.rot)) { var ok = T.moveTo(shape, [it.lat, it.lon], [0, it.lon], it.rot); it.lat = ok[0]; it.lon = ok[1]; }
        items.push(it);
        selected = it;
        if (!opt.silent) { render(); if (!opt.noFit) fitAll(); writeHash(); }
        ae('feature_use', { f: 'add', k: shape.key, t: shape.type });
        return it;
    }
    function removeItem(it) {
        items = items.filter(function (x) { return x !== it; });
        if (selected === it) selected = items[items.length - 1] || null;
        render(); writeHash();
    }
    function homeItem(it) { var c = it.shape.geo.c; it.lat = c[0]; it.lon = c[1]; it.rot = 0; render(); writeHash(); }
    function normRot(r) { return ((Math.round(r) % 360) + 540) % 360 - 180; }
    // every turn — handle, slider, [ ] — goes through the wall (core.turn)
    function turnTo(it, rot) {
        rot = normRot(rot);
        var p = T.turn(it.shape, it.lat, it.lon, rot);
        if (!p) return false;
        it.lat = p[0]; it.lon = p[1]; it.rot = rot;
        return true;
    }

    // ── map + canvas overlay ──────────────────────────────────────────────
    var map, cv, ctx, bounds = null, zoomNow, centerNow, drawn = [], PAD = 0.15;
    var skin = 'light';
    function initMap() {
        var Lf = L;
        map = Lf.map($('tsMap'), { worldCopyJump: true, zoomControl: false, minZoom: 1, maxZoom: 9, zoomSnap: 0.25, zoomDelta: 0.5, preferCanvas: true }).setView([30, 10], 2);
        var rtl = CFG.dir === 'rtl';
        Lf.control.zoom({ position: rtl ? 'topleft' : 'topright' }).addTo(map);
        if (window.__mapTiles) {
            window.__mapTiles.base(Lf, map, null, {
                slug: SLUG, styles: CFG.tile.styles, labels: CFG.tile.labels, controlPosition: rtl ? 'topleft' : 'topright',
                credit: CFG.tile.credit || '', opts: { maxZoom: 19 },
                onStyle: function (m) { skin = m === 'dark' ? 'dark' : 'light'; $('tsMap').setAttribute('data-basemap', skin); draw(); },
                onExhausted: function () { var o = $('tsMapOffline'); if (o) { o.textContent = t('map_offline'); o.hidden = false; } }
            });
        } else if (window.__basemapFallback) {
            window.__basemapFallback(Lf, map, CFG.tile.credit || '');
        }
        cv = document.createElement('canvas');
        cv.className = 'ts-canvas leaflet-zoom-animated';
        cv.style.position = 'absolute';
        cv.style.pointerEvents = 'none';
        ctx = cv.getContext('2d');
        map.getPanes().overlayPane.appendChild(cv);
        map.on('zoomanim', function (e) {
            var scale = map.getZoomScale(e.zoom, zoomNow);
            var off = map.getSize().multiplyBy(0.5 + PAD).multiplyBy(-scale).add(map.project(centerNow, e.zoom)).subtract(map._getNewPixelOrigin(e.center, e.zoom));
            L.DomUtil.setTransform(cv, off, scale);
        });
        map.on('moveend zoomend resize viewreset', reset);
        reset();
        bindPointer();
    }
    function reset() {
        var size = map.getSize(), padPx = size.multiplyBy(PAD).round();
        var min = map.containerPointToLayerPoint(padPx.multiplyBy(-1)).round();
        bounds = { min: min, size: size.add(padPx.multiplyBy(2)) };
        zoomNow = map.getZoom(); centerNow = map.getCenter();
        L.DomUtil.setPosition(cv, min);
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        cv.width = Math.round(bounds.size.x * dpr); cv.height = Math.round(bounds.size.y * dpr);
        cv.style.width = bounds.size.x + 'px'; cv.style.height = bounds.size.y + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        draw();
    }
    var RADI = Math.PI / 180, TILE = 256;
    function worldPx(lat, lon, z) {
        var s = Math.sin(Math.max(-85.05, Math.min(85.05, lat)) * RADI), n = TILE * Math.pow(2, z);
        return [(lon + 180) / 360 * n, (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n];
    }
    function hexA(h, a) { var r = parseInt(h.slice(1, 3), 16), g = parseInt(h.slice(3, 5), 16), b = parseInt(h.slice(5, 7), 16); return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')'; }

    function draw() {
        if (!ctx || !bounds) return;
        ctx.clearRect(0, 0, bounds.size.x, bounds.size.y);
        drawn = [];
        var z = map.getZoom(), po = map.getPixelOrigin(), ox = po.x + bounds.min.x, oy = po.y + bounds.min.y;
        var worldW = TILE * Math.pow(2, z);
        var viewC = map.getCenter();
        // big under small: Greenland dropped onto Africa must stay grabbable
        var order = items.slice().sort(function (a, b) { return b.shape.area - a.shape.area; });
        order.forEach(function (it) {
            var p = T.place(it.shape, it.lat, it.lon, it.rot);
            // put the shape in the world copy closest to the view
            var cpx = worldPx(it.lat, it.lon, z)[0], vpx = worldPx(viewC.lat, viewC.lng, z)[0];
            var shift = Math.round((vpx - cpx) / worldW) * worldW;
            var path = new Path2D(), top = Infinity;
            p.rings.forEach(function (r) {
                for (var i = 0; i < r.lat.length; i++) {
                    var q = worldPx(r.lat[i], r.lon[i], z);
                    var x = q[0] + shift - ox, y = q[1] - oy;
                    if (y < top) top = y;
                    if (i) path.lineTo(x, y); else path.moveTo(x, y);
                }
                path.closePath();
            });
            var sel = it === selected;
            ctx.fillStyle = hexA(it.color, sel ? 0.42 : 0.3);
            ctx.fill(path, 'evenodd');
            ctx.lineJoin = 'round';
            ctx.lineWidth = sel ? 3 : 2;
            ctx.strokeStyle = it.color;
            ctx.stroke(path);
            var c = worldPx(it.lat, it.lon, z), cx = c[0] + shift - ox, cy = c[1] - oy;
            drawn.push({ it: it, path: path, cx: cx, cy: cy, top: top });
        });
        // labels on top of every fill; a label that would land on another one
        // (Greenland dropped onto Africa) steps down until it is free
        var boxes = [];
        drawn.forEach(function (d) {
            var it = d.it;
            var label = name(it.shape), sub = fmtArea(it.shape.area);
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.font = '800 13px system-ui,-apple-system,Segoe UI,sans-serif';
            var w = Math.max(ctx.measureText(label).width, ctx.measureText(sub).width) + 8;
            var ly = d.cy;
            for (var tries = 0; tries < 6; tries++) {
                var clash = boxes.some(function (b) { return Math.abs(b[0] - d.cx) < (b[2] + w) / 2 && Math.abs(b[1] - ly) < 34; });
                if (!clash) break;
                ly += 36;
            }
            boxes.push([d.cx, ly, w]);
            d.ly = ly;
            d.lbox = [d.cx, ly, w];   // the name is part of the shape: people grab it
            ctx.lineWidth = 3.5; ctx.strokeStyle = skin === 'dark' ? 'rgba(10,12,16,.92)' : 'rgba(255,255,255,.95)';
            ctx.strokeText(label, d.cx, ly - 8); ctx.fillStyle = skin === 'dark' ? '#fff' : '#111';
            ctx.fillText(label, d.cx, ly - 8);
            ctx.font = '600 12px system-ui,-apple-system,Segoe UI,sans-serif';
            ctx.strokeText(sub, d.cx, ly + 8); ctx.fillStyle = skin === 'dark' ? '#e5e7eb' : '#333';
            ctx.fillText(sub, d.cx, ly + 8);
            if (it === selected && !quiz) {
                // rotation handle: a knob on a stalk ABOVE the shape's top edge, as in
                // any drawing tool. Over the centre it took the grab meant for moving;
                // over the label it sat over a neighbour's name whenever labels had
                // been stepped apart. A top edge above the visible map puts the knob
                // at the top of the view.
                var visTop = map.containerPointToLayerPoint([0, 0]).y - bounds.min.y;
                var hx = d.cx, hy = Math.max(d.top - 26, visTop + 20);
                ctx.strokeStyle = it.color; ctx.lineWidth = 2;
                if (d.top > hy + 9) { ctx.beginPath(); ctx.moveTo(hx, d.top); ctx.lineTo(hx, hy + 9); ctx.stroke(); }
                ctx.beginPath(); ctx.arc(hx, hy, 9, 0, Math.PI * 2);
                ctx.fillStyle = '#fff'; ctx.fill(); ctx.stroke();
                ctx.fillStyle = it.color; ctx.font = '700 12px system-ui,sans-serif'; ctx.fillText('↻', hx, hy + 0.5);
                d.handle = [hx, hy];
            }
        });
        if (drawPts.length) drawDraft();
    }

    // ── pointer: drag a shape, turn it by its handle ──────────────────────
    var drag = null;
    function canvasPoint(e) {
        var r = map.getContainer().getBoundingClientRect();
        var cp = L.point(e.clientX - r.left, e.clientY - r.top);
        var lp = map.containerPointToLayerPoint(cp);
        return { cp: cp, x: lp.x - bounds.min.x, y: lp.y - bounds.min.y };
    }
    /* What the pointer is ON, topmost first: the turn handle, a name label, the
     * fill, then a few pixels of slack round the outline (a thin coast, a speck
     * like Luxembourg).
     * ⚠️ isPointInPath transforms the PATH by the context's current matrix but
     * takes the POINT in raw bitmap pixels. This context is scaled by
     * devicePixelRatio, so under Windows' 125–150 % scaling every shape was
     * tested as if scaled round the canvas corner: grabbing Russia lying over
     * Africa moved Africa (measured 2026-09-25: 1× right; 1.25×, 1.5×, 2×
     * wrong — the owner's own screen). Tests run under the identity matrix,
     * where path and point are both CSS pixels. */
    function inPath(path, x, y, slack) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        var r;
        if (slack) { ctx.lineWidth = slack; ctx.lineJoin = 'round'; r = ctx.isPointInStroke(path, x, y); }
        else r = ctx.isPointInPath(path, x, y, 'evenodd');
        ctx.restore();
        return r;
    }
    function hit(p) {
        var i, d;
        for (i = drawn.length - 1; i >= 0; i--) {
            d = drawn[i];
            if (d.handle && Math.abs(p.x - d.handle[0]) < 16 && Math.abs(p.y - d.handle[1]) < 16) return { d: d, handle: true };
        }
        // the topmost shape (smaller ones lie on top) whose body OR own name is
        // under the pointer. Names checked in a pass of their own lost Greenland
        // lying on Africa: Africa's name sat on Greenland's body, and a grab at
        // Greenland's middle moved Africa.
        for (i = drawn.length - 1; i >= 0; i--) {
            d = drawn[i];
            if (inPath(d.path, p.x, p.y)) return { d: d };
            if (d.lbox && Math.abs(p.x - d.lbox[0]) <= d.lbox[2] / 2 && Math.abs(p.y - d.lbox[1]) <= 17) return { d: d };
        }
        for (i = drawn.length - 1; i >= 0; i--) if (inPath(drawn[i].path, p.x, p.y, 14)) return { d: drawn[i] };
        return null;
    }
    function bindPointer() {
        var el = map.getContainer();
        var down = function (e) {
            if (drawMode || (e.button != null && e.button !== 0)) return;
            /* A second finger makes a pinch, and the pinch is the map's: on a
             * phone the shapes cover most of the map, and a first finger that
             * landed on one kept the two-finger touch from ever reaching
             * Leaflet — zooming out was impossible (found 2026-09-25). The shape
             * being dragged is let go where it is. */
            if ((e.touches && e.touches.length > 1) || (e.type === 'pointerdown' && e.pointerType === 'touch' && drag && drag.id !== e.pointerId)) { up(); return; }
            var p = canvasPoint(e.touches ? e.touches[0] : e);
            var h = hit(p);
            if (!h) return;
            e.stopPropagation(); if (e.cancelable) e.preventDefault();
            if (e.type !== 'pointerdown') return;
            var it = h.d.it;
            if (quiz && quiz.phase === 'ask') { quizAnswer(it); return; }
            selected = it;
            map.dragging.disable();
            var ll = map.containerPointToLatLng(p.cp);
            var from = T.polar([T.toAuth(it.lat), it.lon], T.toAuth(ll.lat), ll.lng);
            // the turn follows the pointer FROM where it grabbed: an absolute
            // angle snapped a shape already turned by 30° back to 0 on touch
            var ang0 = Math.atan2(p.x - h.d.cx, -(p.y - h.d.cy)) * 180 / Math.PI;
            drag = { it: it, handle: !!h.handle, grab: from, id: e.pointerId, startRot: it.rot, ang0: ang0, moved: false };
            try { el.setPointerCapture(e.pointerId); } catch (_) {}
            el.style.cursor = 'grabbing';
            draw();
            renderList();
        };
        ['pointerdown', 'mousedown', 'touchstart'].forEach(function (ev) { el.addEventListener(ev, down, { capture: true, passive: false }); });
        el.addEventListener('pointermove', function (e) {
            if (!drag) {
                if (e.pointerType === 'mouse' && bounds) { var h = hit(canvasPoint(e)); el.style.cursor = h ? (h.handle ? 'alias' : 'grab') : ''; }
                return;
            }
            var p = canvasPoint(e), it = drag.it;
            if (drag.handle) {
                var d = drawn.filter(function (x) { return x.it === it; })[0];
                if (!d) return;
                var ang = Math.atan2(p.x - d.cx, -(p.y - d.cy)) * 180 / Math.PI;
                turnTo(it, drag.startRot + ang - drag.ang0);
            } else {
                var ll = map.containerPointToLatLng(p.cp);
                // the point under the finger stays under the finger: centre = the
                // grabbed point's offset taken back from the pointer
                var target = T.destination(T.toAuth(ll.lat), ll.lng, drag.grab[0], drag.grab[1] + Math.PI);
                target = [T.fromAuth(target[0]), target[1]];
                var ok = T.moveTo(it.shape, target, [it.lat, it.lon], it.rot);
                it.lat = ok[0]; it.lon = ok[1];
            }
            drag.moved = true;
            draw();
        });
        var up = function () {
            if (!drag) return;
            var it = drag.it, moved = drag.moved, turned = drag.handle;
            drag = null;
            map.dragging.enable();
            el.style.cursor = '';
            if (moved) {
                // a turn changes the equal-area strip too (it is drawn turned)
                renderList(); if (turned) renderCompare(); writeHash(); ae('feature_use', { f: turned ? 'turn' : 'drag' });
                // the hint has done its job once a shape has been dragged
                var hint = $('tsHint'); if (hint && !hint.hidden) { hint.hidden = true; save('hinted', '1'); }
            }
        };
        el.addEventListener('pointerup', up);
        el.addEventListener('pointercancel', up);
        /* The wheel belongs to the PAGE until the map is in use. The map fills
         * most of a laptop screen, so a plain wheel over it zoomed the map and
         * the page under it (how-to, FAQ) could not be scrolled to (found
         * 2026-09-25). A press inside the map wakes it until the pointer leaves;
         * Ctrl/⌘ + wheel — and a touchpad pinch, which arrives as one — always
         * zooms. Leaflet's own wheel listener sits on this element in the bubble
         * phase, so stopping the event here in capture keeps it from zooming
         * while the page still scrolls (nothing is prevented). */
        var awake = false, hintT = 0;
        el.addEventListener('pointerdown', function () { awake = true; }, true);
        el.addEventListener('pointerleave', function () { awake = false; });
        el.addEventListener('wheel', function (e) {
            if (awake || e.ctrlKey || e.metaKey) return;
            e.stopImmediatePropagation(); e.stopPropagation();
            var hint = $('tsWheelHint');
            if (!hint) return;
            hint.classList.add('on');
            clearTimeout(hintT);
            hintT = setTimeout(function () { hint.classList.remove('on'); }, 1600);
        }, { capture: true, passive: true });
        // keyboard on the map: arrows move the selected shape, [ ] turn it
        el.addEventListener('keydown', function (e) {
            if (!selected || drawMode) return;
            var k = e.key, step = e.shiftKey ? 5 : 1;
            var nl = selected.lat, nn = selected.lon;
            if (k === 'ArrowUp') nl += step; else if (k === 'ArrowDown') nl -= step;
            else if (k === 'ArrowLeft') nn -= step; else if (k === 'ArrowRight') nn += step;
            else if (k === '[' || k === ']') {
                turnTo(selected, selected.rot + (k === '[' ? -5 : 5) * step);
                draw(); renderList(); renderCompare(); writeHash(); e.preventDefault(); return;
            }
            else if (k === 'Delete' || k === 'Backspace') { removeItem(selected); e.preventDefault(); return; }
            else return;
            e.preventDefault(); e.stopPropagation();
            var ok = T.moveTo(selected.shape, [nl, nn], [selected.lat, selected.lon], selected.rot);
            selected.lat = ok[0]; selected.lon = ok[1];
            draw(); renderList(); writeHash();
            announce(name(selected.shape) + ': ' + NF1.format(selected.lat) + '°, ' + NF1.format(selected.lon) + '°');
        }, true);
    }
    function announce(msg) { try { if (window.__announce) window.__announce(msg); } catch (_) {} }

    function fitAll() {
        if (!items.length || !map) return;
        var la0 = 90, la1 = -90, lo0 = 540, lo1 = -540;
        items.forEach(function (it) {
            var p = T.place(it.shape, it.lat, it.lon, it.rot);
            p.rings.forEach(function (r) { for (var i = 0; i < r.lat.length; i += 3) { la0 = Math.min(la0, r.lat[i]); la1 = Math.max(la1, r.lat[i]); lo0 = Math.min(lo0, r.lon[i]); lo1 = Math.max(lo1, r.lon[i]); } });
        });
        // Leaflet DROPS an animated fit asked for while a zoom animation runs
        // (_tryAnimatedZoom returns early): a preset chosen during the opening
        // fly-in kept the old view. The fit waits for that animation to end.
        if (map._animatingZoom) {
            if (!fitPending) { fitPending = true; map.once('zoomend', function () { fitPending = false; fitAll(); }); }
            return;
        }
        map.fitBounds([[la0, lo0], [la1, lo1]], { padding: [30, 30], maxZoom: 7 });
    }
    var fitPending = false;

    // ── list, comparison ──────────────────────────────────────────────────
    function render() { draw(); renderList(); renderCompare(); }
    /* One compact row per shape (colour, name, area, remove); the numbers, the
     * Mercator note, the turn and the buttons open only under the selected one.
     * A card per shape made the list 1 339 px tall beside a 600 px map. */
    function renderList() {
        var box = $('tsList');
        box.innerHTML = '';
        $('tsEmpty').hidden = items.length > 0;
        var selRow = null;
        items.forEach(function (it) {
            var sh = it.shape, on = it === selected;
            var row = document.createElement('div');
            row.className = 'ts-item' + (on ? ' on' : '');
            row.innerHTML = '<div class="ts-item-h"><i></i><button type="button" class="ts-item-name"></button>' +
                '<span class="ts-item-sub"></span><button type="button" class="ts-x">✕</button></div>';
            row.querySelector('i').style.background = it.color;
            var nb = row.querySelector('.ts-item-name');
            nb.textContent = name(sh);
            nb.setAttribute('aria-expanded', on ? 'true' : 'false');
            row.querySelector('.ts-item-sub').textContent = fmtArea(sh.area) + ' · ' + t('kind_' + sh.type);
            var x = row.querySelector('.ts-x');
            x.setAttribute('aria-label', t('remove') + ': ' + name(sh));
            x.title = t('remove');
            x.addEventListener('click', function (e) { e.stopPropagation(); removeItem(it); });
            row.querySelector('.ts-item-h').addEventListener('click', function () { selectItem(it, true); });
            if (on) { row.appendChild(itemDetail(it)); selRow = row; }
            box.appendChild(row);
        });
        $('tsClear').hidden = !items.length;
        $('tsCompareP').hidden = items.length < 2;
        // keep the selected row in sight INSIDE the list's own scroll — never the page
        var sc = $('tsScroll');
        if (selRow && sc && sc.scrollHeight > sc.clientHeight + 1) {
            var top = selRow.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop;
            var bottom = top + selRow.offsetHeight;
            if (top < sc.scrollTop) sc.scrollTop = top;
            else if (bottom > sc.scrollTop + sc.clientHeight) sc.scrollTop = Math.min(top, bottom - sc.clientHeight);
        }
        listCue();
    }
    /* A list taller than its panel is cut by the panel's edge; the fade says
     * «more below» instead of a half-drawn button that looks broken. */
    function listCue() {
        var sc = $('tsScroll');
        if (sc) sc.classList.toggle('more', sc.scrollTop + sc.clientHeight < sc.scrollHeight - 2);
    }
    // Within ~12.6° of the equator the stretch rounds to 1 and the sentence read
    // «here — 1 times larger»; it is said in words instead.
    function mercText(it) {
        var here = T.mercatorScale(it.lat), home = T.mercatorScale(it.shape.geo.c[0]);
        var moved = Math.abs(it.lat - it.shape.geo.c[0]) > 0.5, homeTrue = home < 1.05, hereTrue = here < 1.05;
        return homeTrue && (hereTrue || !moved) ? t('merc_equator')
            : !moved ? t('merc_home', { h: NF1.format(home), x: timesWord(home) })
            : hereTrue ? t('merc_here_true', { h: NF1.format(home), x: timesWord(home) })
            : homeTrue ? t('merc_home_true', { n: NF1.format(here), x: timesWord(here) })
            : t('merc_moved', { h: NF1.format(home), n: NF1.format(here), x: timesWord(here) });
    }
    function itemDetail(it) {
        var sh = it.shape;
        var meta = [];
        var pop = fmtPop(sh.pop);
        if (pop) meta.push(t('pop', { p: pop, y: sh.popYear || '' }));
        if (pop && sh.area) meta.push(t('density', { d: fmtDensity(imperial ? sh.pop / (sh.area / 2.589988) : sh.pop / sh.area), u: imperial ? t('u_mi2') : t('u_km2') }));
        var ref = items[0] !== it ? items[0] : items[1];
        var body = document.createElement('div');
        body.className = 'ts-item-body';
        // turn, back home and regions share one row: a row each made the selected
        // card 255 px tall and pushed the next shape under the fold at 1366×768
        body.innerHTML = '<span class="ts-meta"></span><span class="ts-cmp-line"></span><span class="ts-merc"></span>' +
            '<div class="ts-item-acts"><label class="ts-rot"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.34-5.66"/><path d="M20 4v5h-5"/></svg><span class="ts-sr"></span><input type="range" min="-180" max="180" step="1"></label>' +
            '<button type="button" class="tc-btn ghost sm ts-ico" data-a="home"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 11 12 4l9 7"/><path d="M5 10v10h14V10"/></svg></button>' +
            '<button type="button" class="tc-btn ghost sm" data-a="regions" hidden></button></div>';
        body.querySelector('.ts-meta').textContent = meta.join(' · ');
        body.querySelector('.ts-cmp-line').textContent = ref ? ratioText(it, ref) : '';
        var merc = body.querySelector('.ts-merc');
        merc.textContent = mercText(it);
        var rot = body.querySelector('input[type=range]');
        rot.value = String(it.rot);
        rot.setAttribute('aria-label', t('rotate') + ': ' + name(sh));
        body.querySelector('.ts-rot .ts-sr').textContent = t('rotate');
        rot.addEventListener('input', function () { if (!turnTo(it, +rot.value)) rot.value = String(it.rot); draw(); });
        // the list is NOT rebuilt here: a new slider would take the keyboard focus
        // away after the first arrow press; a turn near the top may have slid the
        // shape south, so the Mercator line is refreshed in place
        rot.addEventListener('change', function () { merc.textContent = mercText(it); writeHash(); renderCompare(); });
        var hb = body.querySelector('[data-a=home]');
        hb.title = t('to_home');
        hb.setAttribute('aria-label', t('to_home'));
        hb.addEventListener('click', function () { homeItem(it); fitAll(); });
        if (sh.type === 'c') {
            var rg = body.querySelector('[data-a=regions]');
            rg.hidden = false; rg.textContent = t('regions_of');
            rg.addEventListener('click', function () { openRegions(sh); });
        }
        return body;
    }
    /* Picked in the list: the map comes to the shape only when it is out of
     * view — re-centring on every click threw away the view people had set. */
    function selectItem(it, fromList) {
        selected = it;
        if (fromList && map && !map.getBounds().contains([it.lat, it.lon])) map.panTo([it.lat, it.lon]);
        draw(); renderList();
    }

    /* The honest comparison: every shape in a Lambert equal-area projection
     * around its own centre, one common scale, side by side (or stacked on one
     * centre). Here Greenland is simply small — which Mercator never says. */
    function renderCompare() {
        var c = $('tsCompare');
        if (!c || items.length < 2) return;
        var stacked = $('tsStack').classList.contains('on');
        var W = c.clientWidth || 300, dpr = Math.min(window.devicePixelRatio || 1, 2);
        var shapes = items.map(function (it) { return { it: it, rings: T.lambert(it.shape, it.rot) }; });
        shapes.forEach(function (s) {
            var x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
            s.rings.forEach(function (r) { if (r.hole) return; for (var i = 0; i < r.x.length; i++) { x0 = Math.min(x0, r.x[i]); x1 = Math.max(x1, r.x[i]); y0 = Math.min(y0, r.y[i]); y1 = Math.max(y1, r.y[i]); } });
            s.bb = [x0, y0, x1, y1]; s.w = x1 - x0; s.h = y1 - y0;
        });
        var H, scale, pad = 10;
        if (stacked) {
            var mw = Math.max.apply(null, shapes.map(function (s) { return s.w; })), mh = Math.max.apply(null, shapes.map(function (s) { return s.h; }));
            H = 200; scale = Math.min((W - 2 * pad) / mw, (H - 2 * pad - 16) / mh);
        } else {
            // one row, same scale: the widest row that fits
            var sumW = shapes.reduce(function (a, s) { return a + s.w; }, 0), maxH = Math.max.apply(null, shapes.map(function (s) { return s.h; }));
            // at most 150 px tall: the strip sits under the map and must not outgrow it
            scale = Math.min((W - pad * (shapes.length + 1)) / sumW, 150 / maxH);
            H = Math.max(120, maxH * scale + 2 * pad + 22);
        }
        c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); c.style.height = H + 'px';
        var x = c.getContext('2d');
        x.setTransform(dpr, 0, 0, dpr, 0, 0);
        x.clearRect(0, 0, W, H);
        // the row sits in the middle of the strip, as the stacked shapes do: held to
        // 150 px tall it often fills a third of a wide strip, and drawn from the
        // start it left the shapes on one side and their sentence far on the other
        var cursor = stacked ? pad : Math.max(pad, (W - (sumW * scale + pad * (shapes.length - 1))) / 2);
        var dark = document.documentElement.getAttribute('data-theme') !== 'light';
        shapes.forEach(function (s) {
            var cx, cy;
            if (stacked) { cx = W / 2; cy = (H - 16) / 2; }
            else { cx = cursor + s.w * scale / 2; cy = H - pad - 20 - s.h * scale / 2; cursor += s.w * scale + pad; }
            var mx = (s.bb[0] + s.bb[2]) / 2, my = (s.bb[1] + s.bb[3]) / 2;
            var path = new Path2D();
            s.rings.forEach(function (r) {
                for (var i = 0; i < r.x.length; i++) { var px = cx + (r.x[i] - mx) * scale, py = cy - (r.y[i] - my) * scale; if (i) path.lineTo(px, py); else path.moveTo(px, py); }
                path.closePath();
            });
            x.fillStyle = hexA(s.it.color, stacked ? 0.28 : 0.4); x.fill(path, 'evenodd');
            x.strokeStyle = s.it.color; x.lineWidth = 1.6; x.stroke(path);
            if (!stacked) {
                x.fillStyle = dark ? '#e5e7eb' : '#222'; x.font = '700 12px system-ui,sans-serif'; x.textAlign = 'center'; x.textBaseline = 'top';
                var nm = name(s.it.shape);
                if (x.measureText(nm).width > s.w * scale + pad) nm = nm.slice(0, Math.max(3, Math.floor((s.w * scale + pad) / 7))) + '…';
                x.fillText(nm, cx, H - 18);
            }
        });
        // the sentence people come for
        var sorted = items.slice().sort(function (a, b) { return b.shape.area - a.shape.area; });
        var lines = [];
        for (var i = 1; i < sorted.length && i < 5; i++) lines.push(ratioText(sorted[0], sorted[i]));
        var sum = items.slice(1).reduce(function (a, it) { return a + it.shape.area; }, 0);
        if (items.length > 2) lines.push(t('sum_line', { a: name(items[0].shape), p: NF.format(sum / items[0].shape.area * 100) }));
        $('tsCompareText').textContent = lines.join(' ');
    }

    // ── search ────────────────────────────────────────────────────────────
    var sugg = [], suggI = -1;
    function norm(s) { return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ё/g, 'е'); }
    function search(q) {
        q = norm(q.trim());
        if (!q) return [];
        var pool = catalog.slice();
        for (var k in regionsLoaded) pool = pool.concat(regionsLoaded[k]);
        var out = [];
        pool.forEach(function (s) {
            var hay = norm(s.search + '|' + (s.names[LOC] || ''));
            var nm = norm(name(s));
            var score = nm.indexOf(q) === 0 ? 3 : hay.indexOf('|' + q) >= 0 || hay.indexOf(q) === 0 ? 2 : hay.indexOf(q) >= 0 ? 1 : 0;
            if (score) out.push({ s: s, score: score });
        });
        out.sort(function (a, b) { return b.score - a.score || b.s.area - a.s.area; });
        return out.slice(0, 12).map(function (x) { return x.s; });
    }
    function renderSugg() {
        var box = $('tsSugg');
        box.innerHTML = '';
        sugg.forEach(function (s, i) {
            var li = document.createElement('li');
            li.setAttribute('role', 'option');
            li.id = 'tsOpt' + i;
            li.className = i === suggI ? 'on' : '';
            li.setAttribute('aria-selected', i === suggI ? 'true' : 'false');
            var parent = s.parent && byKey[s.parent] ? ' · ' + name(byKey[s.parent]) : '';
            li.innerHTML = '<span></span><small></small>';
            li.firstChild.textContent = name(s);
            li.lastChild.textContent = t('kind_' + s.type) + parent + ' · ' + fmtArea(s.area);
            li.addEventListener('mousedown', function (e) { e.preventDefault(); pick(s); });
            box.appendChild(li);
        });
        box.hidden = !sugg.length;
        $('tsSearch').setAttribute('aria-expanded', sugg.length ? 'true' : 'false');
        $('tsSearch').setAttribute('aria-activedescendant', suggI >= 0 ? 'tsOpt' + suggI : '');
    }
    function pick(s) {
        $('tsSearch').value = '';
        sugg = []; suggI = -1; renderSugg();
        if (quiz) endQuiz();
        addShape(s);
    }
    function openRegions(country) {
        var btn = $('tsSearch');
        loadRegions(country.a3).then(function (list) {
            btn.focus();
            btn.value = '';
            sugg = list.slice().sort(function (a, b) { return b.area - a.area; }).slice(0, 60);
            suggI = -1;
            renderSugg();
            $('tsSuggHead').textContent = t('regions_head', { c: name(country), n: list.length });
            $('tsSuggHead').hidden = false;
        }, function () { if (window.__toast) window.__toast(t('load_failed'), 'error'); });
    }

    // ── presets ───────────────────────────────────────────────────────────
    var PRESETS = {
        'greenland-africa': [['GRL', 2, 22], ['C:AF']],
        'russia-africa': [['RUS', 4, 22], ['C:AF']],
        // Packed offline on an equal-area raster (tests/scratch: greedy search,
        // rotations kept within ±60° so every country stays recognisable):
        // 77 % of Africa covered, each shape 59–100 % inside it.
        'africa-fits': [['C:AF'], ['CHN', 6.3, 23.55, 41], ['X:US48', 26.5, -0.2, -60], ['IND', -17.1, 25.35, -33], ['MEX', 22, 26.85, 32], ['PER', -6.95, 37.05, 16], ['FRA', 9.35, 3.15, -36], ['ESP', 15.2, 35.8, -58]],
        'alaska-us': [['US-AK', 38, -98], ['X:US48']],
        'yakutia-argentina': [['RU-SA', -36, -64], ['ARG']],
        'texas-europe': [['US-TX', 48, 10], ['DEU'], ['FRA']],
        'uk-madagascar': [['GBR', -19, 47], ['MDG']],
        'korea-uk': [['KOR', 53, -2], ['PRK', 55.5, -3.5], ['GBR']],
        'indonesia-europe': [['IDN', 49, 12], ['C:EU']],
        'brazil-usa': [['BRA', 40, -98], ['X:US48']],
        'france-madagascar': [['FRA', -19, 47], ['MDG']],
        'germany-texas': [['DEU', 31, -99], ['US-TX']],
        'spain-mexico': [['ESP', 23, -102], ['MEX']],
        'saudi-europe': [['SAU', 48, 5], ['C:EU']],
        'ukraine-france': [['UKR', 46.5, 2.5], ['FRA']],
        'canada-brazil': [['CAN', -10, -52], ['BRA']],
        'australia-usa': [['AUS', 39, -98], ['X:US48']]
    };
    function applyPreset(key) {
        var P = PRESETS[key];
        if (!P) return;
        endQuiz();
        items = []; selected = null;
        var needs = P.map(function (x) { return x[0]; }).filter(function (k) { return !byKey[k]; });
        var loads = needs.map(function (k) {
            var a3 = /^RU-/.test(k) ? 'RUS' : /^US-/.test(k) ? 'USA' : null;
            return a3 ? loadRegions(a3) : Promise.resolve();
        });
        Promise.all(loads).then(function () {
            P.forEach(function (x) {
                var sh = byKey[x[0]];
                if (!sh) return;
                addShape(sh, { lat: x[1], lon: x[2], rot: x[3] || 0, silent: true });
            });
            selected = items[0] || null;
            render(); fitAll(); writeHash();
            ae('feature_use', { f: 'preset', k: key });
        });
    }

    // ── share link: state in the hash, REPLACED (never a history entry per drag) ──
    var hashTimer = 0;
    /* A drawn shape exists on no server: its outline travels IN the link
     * (U~lat_lon~…, 4 decimals ≈ 11 m). By key alone the link opened without it
     * — «the link opens exactly what you built» failed for exactly the shape
     * nobody else has (found 2026-09-25). */
    function hashKey(sh) {
        if (sh.type !== 'u') return encodeURIComponent(sh.key);
        var o = sh.parts[0].outer;
        return 'U~' + T.encodeOutline(o.lat, o.lon);
    }
    function stateHash() {
        var s = items.map(function (it) { return hashKey(it.shape) + '@' + it.lat.toFixed(3) + ',' + it.lon.toFixed(3) + ',' + Math.round(it.rot); }).join('|');
        return s ? '#s=' + s + '&g=2' : '';
    }
    function writeHash() {
        clearTimeout(hashTimer);
        hashTimer = setTimeout(function () {
            try { history.replaceState(history.state, '', location.pathname + location.search + stateHash()); } catch (_) {}
        }, 250);
    }
    function userShapeFromKey(k) {
        var o = T.decodeOutline(k.slice(2));
        return o ? userShape(o.lat, o.lon) : null;
    }
    function readHash() {
        var m = /[#&]s=([^&]+)/.exec(location.hash);
        if (!m) return Promise.resolve(false);
        /* A link without g=2 was made before 2026-10-22, when the region set had the two
         * Moscow codes crossed (RU-MOW was the oblast): it opens the shape it showed then. */
        var crossed = !/[#&]g=2(?:&|$)/.test(location.hash);
        var parts = m[1].split('|').map(function (p) {
            var at = p.lastIndexOf('@'); var k = decodeURIComponent(at > 0 ? p.slice(0, at) : p); var v = (at > 0 ? p.slice(at + 1) : '').split(',');
            if (crossed && (k === 'RU-MOW' || k === 'RU-MOS')) k = k === 'RU-MOW' ? 'RU-MOS' : 'RU-MOW';
            return { k: k, lat: parseFloat(v[0]), lon: parseFloat(v[1]), rot: parseFloat(v[2]) || 0 };
        }).filter(function (x) { return x.k; }).slice(0, 24);
        var need = {};
        parts.forEach(function (x) { var m2 = /^([A-Z]{2})-/.exec(x.k); if (m2 && !byKey[x.k]) { var c = catalog.filter(function (s) { return s.a2 === m2[1]; })[0]; if (c) need[c.a3] = 1; } });
        return Promise.all(Object.keys(need).map(loadRegions)).then(function () {
            parts.forEach(function (x) { var sh = /^U~/.test(x.k) ? userShapeFromKey(x.k) : byKey[x.k]; if (sh) addShape(sh, { lat: isFinite(x.lat) ? x.lat : null, lon: isFinite(x.lon) ? x.lon : null, rot: x.rot, silent: true }); });
            selected = items[0] || null;
            render(); fitAll();
            ae('feature_use', { f: 'share_open', n: items.length });
            return items.length > 0;
        });
    }
    /* The copy runs inside the click itself: Safari and Firefox honour the
     * clipboard only during the user's gesture, and the old 300 ms wait for the
     * debounced address could fall outside it (then only a prompt was left). */
    function share() {
        clearTimeout(hashTimer);
        var url = location.origin + location.pathname + location.search + stateHash();
        try { history.replaceState(history.state, '', url); } catch (_) {}
        var done = function () { if (window.__toast) window.__toast(t('share_copied'), 'success'); };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { prompt(t('share_manual'), url); });
        else prompt(t('share_manual'), url);
        ae('feature_use', { f: 'share', n: items.length });
    }

    // ── quiz: pairs Mercator gets wrong ───────────────────────────────────
    function quizPairs(n) {
        var pool = catalog.filter(function (s) { return s.type === 'c' && s.area > 60000; });
        pool.forEach(function (s) { T.prepare(s); s._look = s.area * T.mercatorScale(s.geo.c[0]); });
        var out = [], tries = 0;
        while (out.length < n && tries++ < 5000) {
            var a = pool[Math.floor(Math.random() * pool.length)], b = pool[Math.floor(Math.random() * pool.length)];
            if (a === b) continue;
            var tr = a.area / b.area, lk = a._look / b._look;
            // the smaller one must LOOK bigger on the map, by a margin people notice
            if (tr > 1.15 && tr < 4 && lk < 0.85) { if (!out.some(function (p) { return p[0] === b || p[1] === b; })) out.push(Math.random() < 0.5 ? [a, b] : [b, a]); }
        }
        return out;
    }
    /* The quiz borrows the map. Closing it used to leave the map EMPTY: the
     * shapes someone had been arranging were gone (found 2026-09-25) — they are
     * kept aside and given back on every way out (close, a preset, a search). */
    var quizSaved = null;
    function startQuiz() {
        // Pairs come from the country catalog, and the button is live before it arrives:
        // an early click used to end the round at once with «0 of 0 correct».
        if (!catalog.length) {
            if (worldReady) worldReady.then(function () { if (catalog.length) startQuiz(); }, function () {});
            return;
        }
        if (!quiz) quizSaved = { items: items, selected: selected };
        quiz = { pairs: quizPairs(10), i: 0, score: 0, phase: 'ask' };
        $('tsQuiz').hidden = false;
        ae('feature_use', { f: 'quiz_start' });
        quizRound();
    }
    function quizRound() {
        var q = quiz, pair = q.pairs[q.i];
        if (!pair) { quizEnd(); return; }
        items = []; selected = null;
        q.items = [addShape(pair[0], { silent: true }), addShape(pair[1], { silent: true })];
        selected = null;
        q.phase = 'ask';
        $('tsQuizQ').textContent = t('quiz_q', { i: q.i + 1, n: q.pairs.length, a: name(pair[0]), b: name(pair[1]) });
        $('tsQuizA').textContent = '';
        $('tsQuizA').className = 'ts-quiz-a';
        $('tsQuizNext').hidden = true;
        $('tsQuizPickA').hidden = $('tsQuizPickB').hidden = false;
        $('tsQuizPickA').textContent = name(pair[0]); $('tsQuizPickB').textContent = name(pair[1]);
        render(); fitAll();
    }
    // The answer names a shape of the QUESTION (pair[0] or pair[1]), not whatever is on
    // the map now: the map stays editable during a round, and the buttons used to pass
    // items[0]/items[1] — after «Clear» or a removed shape the answer pointed at nothing
    // (Sentry TIMBRICA-FRONTEND-MX, «reading 'shape'»).
    function quizAnswer(idx) {
        var q = quiz;
        if (!q || q.phase !== 'ask') return;
        var pair = q.pairs[q.i], big = pair[0].area > pair[1].area ? pair[0] : pair[1];
        var right = pair[idx] === big;
        if (right) q.score++;
        q.phase = 'shown';
        var small = big === pair[0] ? pair[1] : pair[0];
        $('tsQuizA').textContent = (right ? t('quiz_right') : t('quiz_wrong')) + ' ' + t('ratio_bigger', { a: name(big), b: name(small), r: fmtRatio(big.area / small.area), x: timesWord(big.area / small.area) });
        $('tsQuizA').className = 'ts-quiz-a ' + (right ? 'good' : 'bad');
        $('tsQuizPickA').hidden = $('tsQuizPickB').hidden = true;
        $('tsQuizNext').hidden = false;
        // side by side on the equator: the honest picture — of the round's own shapes,
        // put back on the map if they were cleared or removed meanwhile
        var a = q.items[0], b = q.items[1];
        if (items.indexOf(a) < 0) items.push(a);
        if (items.indexOf(b) < 0) items.push(b);
        var sep = 3 + Math.sqrt(Math.max(a.shape.area, b.shape.area)) / 111 * 0.9;
        a.lat = 2; a.lon = 20 - sep / 2; b.lat = 2; b.lon = 20 + sep / 2;
        render(); fitAll();
        ae('feature_use', { f: 'quiz_answer', ok: right ? 1 : 0 });
    }
    function quizEnd() {
        $('tsQuizQ').textContent = t('quiz_done', { s: quiz.score, n: quiz.pairs.length });
        $('tsQuizA').textContent = '';
        $('tsQuizNext').hidden = true;
        $('tsQuizPickA').hidden = $('tsQuizPickB').hidden = true;
        $('tsQuizAgain').hidden = false;
        ae('feature_use', { f: 'quiz_done', s: quiz.score });
    }
    function endQuiz() {
        if (!quiz) return false;
        quiz = null;
        $('tsQuiz').hidden = true;
        $('tsQuizAgain').hidden = true;
        if (quizSaved) { items = quizSaved.items; selected = quizSaved.selected; quizSaved = null; }
        return true;
    }

    // ── draw your own shape ───────────────────────────────────────────────
    var drawMode = false, drawPts = [];
    function startDraw() {
        if (endQuiz()) { render(); fitAll(); }
        drawMode = true; drawPts = [];
        $('tsDrawBar').hidden = false;
        $('tsDrawDone').disabled = true;
        map.getContainer().style.cursor = 'crosshair';
        map.doubleClickZoom.disable();
        ae('feature_use', { f: 'draw_start' });
    }
    function stopDraw() {
        drawMode = false; drawPts = [];
        $('tsDrawBar').hidden = true;
        map.getContainer().style.cursor = '';
        map.doubleClickZoom.enable();
        draw();
    }
    var userN = 0;
    function userShape(lat, lon) {
        var shape = { key: 'U' + (++userN), type: 'u', area: Math.round(T.ringArea(lat, lon) * 10) / 10, pop: null, names: {}, search: '', parts: [{ outer: { lat: Float64Array.from(lat), lon: Float64Array.from(lon) }, holes: [] }] };
        shape.names[LOC] = t('my_shape');
        return shape;
    }
    function finishDraw() {
        if (drawPts.length < 3) return;
        var lat = drawPts.map(function (p) { return p[0]; }), lon = drawPts.map(function (p) { return p[1]; });
        var shape = userShape(lat, lon);
        // three clicks on one line enclose nothing: an area of 0 made «∞ times»
        if (!(shape.area > 0)) return;
        stopDraw();
        addShape(shape, { noFit: true });
        ae('feature_use', { f: 'draw_done', pts: lat.length });
    }
    function drawDraft() {
        var z = map.getZoom(), po = map.getPixelOrigin(), ox = po.x + bounds.min.x, oy = po.y + bounds.min.y;
        ctx.strokeStyle = '#D55E00'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
        ctx.beginPath();
        drawPts.forEach(function (p, i) { var q = worldPx(p[0], p[1], z); if (i) ctx.lineTo(q[0] - ox, q[1] - oy); else ctx.moveTo(q[0] - ox, q[1] - oy); });
        ctx.stroke(); ctx.setLineDash([]);
        drawPts.forEach(function (p) { var q = worldPx(p[0], p[1], z); ctx.beginPath(); ctx.arc(q[0] - ox, q[1] - oy, 4, 0, Math.PI * 2); ctx.fillStyle = '#D55E00'; ctx.fill(); });
    }

    // ── picture ───────────────────────────────────────────────────────────
    function downloadImage() {
        if (!items.length) return;
        // one picture at a time; the button says it is working while tiles load
        var btn = $('tsImage');
        if (btn.getAttribute('aria-busy') === 'true') return;
        btn.setAttribute('aria-busy', 'true');
        var release = function () { btn.removeAttribute('aria-busy'); };
        var size = map.getSize(), scale = 2, W = size.x * scale, H = size.y * scale;
        // legend rows are laid out first, so the canvas is exactly as tall as it needs
        var c = document.createElement('canvas');
        var x = c.getContext('2d');
        var font = (700 + ' ' + 15 * scale) + 'px system-ui,sans-serif';
        x.font = font;
        var rows = [[]], rowW = 20 * scale;
        items.slice(0, 8).forEach(function (it) {
            var label = name(it.shape) + ' — ' + fmtArea(it.shape.area), w = x.measureText(label).width + 50 * scale;
            if (rowW + w > W - 20 * scale && rows[rows.length - 1].length) { rows.push([]); rowW = 20 * scale; }
            rows[rows.length - 1].push({ it: it, label: label }); rowW += w;
        });
        var legendH = rows.length * 28 * scale + 14 * scale, footH = 30 * scale;
        c.width = W; c.height = H + legendH + footH;
        x = c.getContext('2d');
        x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height);
        var zf = map.getZoom(), k = 1;
        var cpx = worldPx(map.getCenter().lat, map.getCenter().lng, zf);
        /* Подложка картинки — тот же кадр, что на экране, отрисованный общим
           примитивом во внеэкранной карте (вектор OpenFreeMap; без WebGL —
           плитки OpenStreetMap). Плиток-картинок у векторной подложки нет,
           поэтому собирать кадр из них, как раньше из CARTO, нечем. */
        var mapSource = 'vector';
        var basemap = window.__mapTiles && window.__mapTiles.snapshot
            ? window.__mapTiles.snapshot({ style: 'street', zoom: zf, x: cpx[0] - size.x / 2, y: cpx[1] - size.y / 2, width: size.x, height: size.y, pixelRatio: scale, timeout: 12000 })
                .then(function (bm) { if (bm) { x.drawImage(bm, 0, 0, W, H); mapSource = bm.mapSource || mapSource; } }, function () {})
            : Promise.resolve();
        basemap.then(function () {
            items.forEach(function (it) {
                var p = T.place(it.shape, it.lat, it.lon, it.rot);
                var path = new Path2D();
                var vpx = worldPx(map.getCenter().lat, map.getCenter().lng, zf)[0], ipx = worldPx(it.lat, it.lon, zf)[0], ww = TILE * Math.pow(2, zf);
                var shift = Math.round((vpx - ipx) / ww) * ww;
                p.rings.forEach(function (r) {
                    for (var i = 0; i < r.lat.length; i++) {
                        var q = worldPx(r.lat[i], r.lon[i], zf), px = (q[0] + shift - (cpx[0] * k - size.x / 2)) * scale, py = (q[1] - (cpx[1] * k - size.y / 2)) * scale;
                        if (i) path.lineTo(px, py); else path.moveTo(px, py);
                    }
                    path.closePath();
                });
                x.fillStyle = hexA(it.color, 0.35); x.fill(path, 'evenodd');
                x.strokeStyle = it.color; x.lineWidth = 2 * scale; x.stroke(path);
            });
            // legend strip under the map
            x.font = font; x.textBaseline = 'middle'; x.textAlign = 'left';
            rows.forEach(function (row, ri) {
                var y0 = H + 22 * scale + ri * 28 * scale, cx = 20 * scale;
                row.forEach(function (e) {
                    x.fillStyle = e.it.color; x.fillRect(cx, y0 - 7 * scale, 14 * scale, 14 * scale);
                    x.fillStyle = '#111'; x.fillText(e.label, cx + 20 * scale, y0);
                    cx += x.measureText(e.label).width + 50 * scale;
                });
            });
            x.font = (600 + ' ' + 12 * scale) + 'px system-ui,sans-serif'; x.fillStyle = '#6b7280'; x.textAlign = 'right';
            var mapCredit = window.__mapTiles && window.__mapTiles.creditText ? window.__mapTiles.creditText(mapSource) : '© OpenStreetMap contributors';
            x.fillText(mapCredit + ' · Natural Earth · ' + (CFG.brand || '') + ' ' + location.host, W - 14 * scale, H + legendH + 14 * scale);
            c.toBlob(function (b) {
                release();
                if (!b) return;
                var nm = items.slice(0, 3).map(function (it) { return name(it.shape); }).join(' - ');
                window.__downloadFile(b, (window.__outputName && window.__outputName.sanitize ? window.__outputName.sanitize(nm) : nm) + '.png');
                try { if (window.__aeToolComplete) window.__aeToolComplete({ kind: 'png', n: items.length }); } catch (_) {}
            }, 'image/png');
        }).catch(function () { release(); });
    }

    // ── wiring ────────────────────────────────────────────────────────────
    function bind() {
        var inp = $('tsSearch');
        inp.addEventListener('input', function () { $('tsSuggHead').hidden = true; sugg = search(inp.value); suggI = sugg.length ? 0 : -1; renderSugg(); });
        inp.addEventListener('keydown', function (e) {
            if (e.key === 'ArrowDown') { suggI = Math.min(sugg.length - 1, suggI + 1); renderSugg(); e.preventDefault(); }
            else if (e.key === 'ArrowUp') { suggI = Math.max(0, suggI - 1); renderSugg(); e.preventDefault(); }
            else if (e.key === 'Enter' && sugg[suggI]) { pick(sugg[suggI]); e.preventDefault(); }
            else if (e.key === 'Escape') { sugg = []; renderSugg(); $('tsSuggHead').hidden = true; }
        });
        inp.addEventListener('blur', function () { setTimeout(function () { sugg = []; renderSugg(); $('tsSuggHead').hidden = true; }, 150); });
        document.querySelectorAll('[data-preset]').forEach(function (b) { b.addEventListener('click', function () { applyPreset(b.getAttribute('data-preset')); }); });
        $('tsClear').addEventListener('click', function () { items = []; selected = null; render(); writeHash(); });
        $('tsFit').addEventListener('click', fitAll);
        $('tsShare').addEventListener('click', share);
        $('tsImage').addEventListener('click', downloadImage);
        $('tsQuizBtn').addEventListener('click', startQuiz);
        $('tsQuizPickA').addEventListener('click', function () { quizAnswer(0); });
        $('tsQuizPickB').addEventListener('click', function () { quizAnswer(1); });
        $('tsQuizNext').addEventListener('click', function () { quiz.i++; quizRound(); });
        $('tsQuizAgain').addEventListener('click', function () { $('tsQuizAgain').hidden = true; startQuiz(); });
        $('tsQuizClose').addEventListener('click', function () { endQuiz(); render(); fitAll(); });
        $('tsDraw').addEventListener('click', startDraw);
        $('tsDrawDone').addEventListener('click', finishDraw);
        $('tsDrawCancel').addEventListener('click', stopDraw);
        $('tsStack').addEventListener('click', function () { var on = !this.classList.contains('on'); this.classList.toggle('on', on); this.setAttribute('aria-pressed', on ? 'true' : 'false'); renderCompare(); });
        map.on('click', function (e) {
            if (!drawMode) return;
            drawPts.push([e.latlng.lat, e.latlng.lng]);
            $('tsDrawDone').disabled = drawPts.length < 3;
            $('tsDrawCount').textContent = t('draw_count', { n: drawPts.length });
            draw();
        });
        map.on('dblclick', function () { if (drawMode) finishDraw(); });
        document.querySelectorAll('#tsUnits [data-u]').forEach(function (b) {
            b.classList.toggle('on', (b.getAttribute('data-u') === 'mi') === imperial);
            b.addEventListener('click', function () {
                imperial = b.getAttribute('data-u') === 'mi'; save('units', imperial ? 'mi' : 'km');
                document.querySelectorAll('#tsUnits [data-u]').forEach(function (x) { x.classList.toggle('on', x === b); });
                render();
            });
        });
        /* The map's height follows the console (container queries, svh) and the
         * comparison strip's width the grid — neither is a window resize, and
         * Leaflet only listens to the window. */
        $('tsScroll').addEventListener('scroll', listCue, { passive: true });
        if (typeof ResizeObserver === 'function') {
            var cmpW = 0;
            new ResizeObserver(function () { if (map) map.invalidateSize({ pan: false }); }).observe($('tsMap'));
            new ResizeObserver(function () { var w = $('tsCompare').clientWidth; if (w && w !== cmpW) { cmpW = w; renderCompare(); } }).observe($('tsCompare'));
            new ResizeObserver(listCue).observe($('tsScroll'));
        } else window.addEventListener('resize', function () { renderCompare(); listCue(); });
        try { new MutationObserver(function () { renderCompare(); }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] }); } catch (_) {}
    }

    function init() {
        if (!$('tsApp') || typeof L === 'undefined' || !T) return;
        if (load('hinted', '') === '1' && $('tsHint')) $('tsHint').hidden = true;
        var wh = $('tsWheelHint');
        if (wh) wh.textContent = t('wheel_hint', { key: /Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘' : 'Ctrl' });
        initMap();
        bind();
        $('tsLoading').hidden = false;
        worldReady = loadWorld();
        worldReady.then(function () {
            $('tsLoading').hidden = true;
            return readHash();
        }).then(function (had) {
            if (!had) applyPreset(CFG.defaultPreset || 'greenland-africa');
        }).catch(function () {
            $('tsLoading').textContent = t('load_failed');
        });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
    window.__trueSize = { get items() { return items; },
        // test hook: where an item's centre is on screen (client coordinates)
        centerPx: function (i) { var it = items[i]; if (!it || !map) return null; var p = map.latLngToContainerPoint([it.lat, it.lon]), r = map.getContainer().getBoundingClientRect(); return { x: r.left + p.x, y: r.top + p.y }; }, addByKey: function (k, o) { return byKey[k] ? addShape(byKey[k], o) : null; },
        handlePx: function () { var d = drawn.filter(function (x) { return x.handle; })[0]; if (!d || !map || !bounds) return null; var cp = map.layerPointToContainerPoint(L.point(d.handle[0] + bounds.min.x, d.handle[1] + bounds.min.y)); return { x: cp.x, y: cp.y, key: d.it.shape.key }; },
        get zoom() { return map ? map.getZoom() : null; },
        // test hook: the cost of one full redraw, ms
        redraw: function () { var t = performance.now(); draw(); return performance.now() - t; },
        preset: applyPreset, loadRegions: loadRegions, get catalog() { return catalog; } };
})();
